import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { MotionBox, hoverScale } from "@rusha/shared-theme";
import CodeIcon from "@mui/icons-material/Code";

const MotionTypography = motion.create(Typography);

const typingVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const terminalLines = [
  {
    text: "? Select template type: ",
    highlight: "API + Database",
    color: "#9cdcfe",
    delay: 0.8,
  },
  {
    text: "? Choose cloud provider: ",
    highlight: "AWS",
    color: "#9cdcfe",
    delay: 1.3,
  },
  {
    text: "? Select database: ",
    highlight: "PostgreSQL",
    color: "#9cdcfe",
    delay: 1.8,
  },
  {
    text: "? Set up CI/CD pipeline: ",
    highlight: "Yes",
    color: "#9cdcfe",
    delay: 2.3,
  },
];

const statusLines = [
  { text: "Initializing new deployment...", color: "#dcdcaa", delay: 3 },
  { text: "✓ Template loaded", color: "#b5cea8", delay: 3.5 },
  { text: "✓ Resources configured", color: "#b5cea8", delay: 4 },
  { text: "✓ Pipeline created", color: "#b5cea8", delay: 4.5 },
];

const VSCodeExtension: React.FC = () => {
  const theme = useTheme();
  return (
    <Box
      id="vscode-extension"
      component="section"
      sx={{
        py: 10,
        backgroundColor: theme.palette.grey[900],
        color: "white",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={4}>
          {/* Terminal window */}
          <Box sx={{ width: { xs: "100%", md: "50%" } }}>
            <MotionBox
              sx={{
                p: 4,
                borderRadius: 4,
                bgcolor: "#1E1E1E",
                boxShadow: "0 20px 80px rgba(0, 0, 0, 0.3)",
                overflow: "auto",
                minHeight: { xs: 380, md: 420 },
                maxHeight: { xs: 420, md: 460 },
                position: "relative",
              }}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              {/* Window chrome */}
              <Box
                sx={{
                  display: "flex",
                  mb: 3,
                  gap: 1,
                  alignItems: "center",
                  position: "sticky",
                  top: 0,
                  bgcolor: "#1E1E1E",
                  pb: 1,
                  zIndex: 2,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "#ff5f56",
                  }}
                />
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "#ffbd2e",
                  }}
                />
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "#27c93f",
                  }}
                />
                <Box sx={{ ml: 2, fontSize: "12px", color: "#75715e" }}>
                  rusha_deployment.sh
                </Box>
              </Box>

              {/* Terminal content */}
              <Box
                sx={{
                  fontFamily: "monospace",
                  fontSize: "14px",
                  position: "relative",
                  pb: 3,
                }}
              >
                <Typography variant="body2" sx={{ color: "#c6937c", mb: 1 }}>
                  $ rusha init
                </Typography>

                {terminalLines.map((line, i) => (
                  <MotionTypography
                    key={i}
                    variant="body2"
                    sx={{ color: line.color, mb: 1 }}
                    variants={typingVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: line.delay }}
                  >
                    {line.text}
                    <Box component="span" sx={{ color: "#ce9178" }}>
                      {line.highlight}
                    </Box>
                  </MotionTypography>
                ))}

                <Box sx={{ height: 20 }} />

                {statusLines.map((line, i) => (
                  <MotionTypography
                    key={i}
                    variant="body2"
                    sx={{ color: line.color, mb: 1 }}
                    variants={typingVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: line.delay }}
                  >
                    {line.text}
                  </MotionTypography>
                ))}

                <MotionTypography
                  variant="body2"
                  sx={{ color: "#6a9955", mt: 2 }}
                  variants={typingVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: 5 }}
                >
                  Deployment ready! Run{" "}
                  <Box component="span" sx={{ color: "#569cd6" }}>
                    rusha deploy
                  </Box>{" "}
                  to proceed.
                </MotionTypography>
              </Box>
            </MotionBox>
          </Box>

          {/* Text content */}
          <Box sx={{ width: { xs: "100%", md: "50%" } }}>
            <MotionBox
              sx={{ position: "relative", zIndex: 1 }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <MotionTypography
                variant="h3"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                Deploy Straight from VSCode
              </MotionTypography>

              <MotionTypography
                variant="body1"
                sx={{ mb: 4, opacity: 0.8, fontSize: "1.1rem" }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                Use our powerful VSCode extension to deploy templates directly
                from your development environment. Configure, customize and
                deploy with a few clicks.
              </MotionTypography>

              <Box component="ul" sx={{ mb: 4, pl: 0, listStyleType: "none" }}>
                {[
                  "One-click deployment to multiple cloud providers",
                  "Template customization with intuitive UI",
                  "Built-in validation and configuration helpers",
                  "Real-time deployment status and logs",
                ].map((item, index) => (
                  <Box
                    component="li"
                    key={index}
                    sx={{
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <MotionBox
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: 0.2 + index * 0.1 }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "white",
                          }}
                        />
                      </Box>
                      <Typography variant="body1">{item}</Typography>
                    </MotionBox>
                  </Box>
                ))}
              </Box>

              <MotionBox {...hoverScale.scaleLg}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  component="a"
                  href="https://marketplace.visualstudio.com/items?itemName=rusha.rusha-vscode"
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<CodeIcon />}
                >
                  Install Extension
                </Button>
              </MotionBox>
            </MotionBox>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default VSCodeExtension;
